import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile — the "confirm you're human" check on the public form.
 *
 * Loads Cloudflare's script once, renders the widget, and hands the token
 * back. The token is single-use and expires, so the widget resets itself and
 * re-reports on expiry. The site key is PUBLIC by design; the matching secret
 * lives only in the Worker.
 */
const SITE_KEY = "0x4AAAAAAFKHQI4MEOzeK9Qb";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
  }
}

function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve) => existing.addEventListener("load", () => resolve(), { once: true }));
  }
  return new Promise((resolve) => {
    const el = document.createElement("script");
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.addEventListener("load", () => resolve(), { once: true });
    document.head.appendChild(el);
  });
}

export function TurnstileWidget({ onVerify }: { onVerify: (token: string) => void }) {
  const box = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let widgetId: string | undefined;
    let cancelled = false;
    void loadScript().then(() => {
      if (cancelled || !box.current || !window.turnstile) {
        if (!cancelled && !window.turnstile) setFailed(true);
        return;
      }
      widgetId = window.turnstile.render(box.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => onVerify(token),
        "expired-callback": () => onVerify(""),
        "error-callback": () => {
          onVerify("");
          setFailed(true);
        },
        theme: "light",
      });
    });
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onVerify]);

  return (
    <div>
      <div ref={box} />
      {failed && (
        <p className="mt-2 text-sm text-muted">
          The human check could not load. Refresh the page, or email us directly.
        </p>
      )}
    </div>
  );
}
